import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  signal,
} from '@angular/core';
import {
  AbstractControl,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { BusinessPurpose } from 'src/app/models/ticket/ticket.model';
import { SharedModule } from 'src/app/shared/shared.module';
import { Contact } from '../../../models/contact.model';
import { Service, ServiceSelectMode } from '../../../models/service.model';
import { CalendarHeaderComponent } from '../../../shared/components/calendar-header/calendar-header.component';
import {
  InlineErrors,
  shouldShowInlineError,
} from '../../../shared/utils/form-validation.util';
import {
  createBusinessDateValidator,
  getNextValidBusinessDate,
  isObservedHoliday,
  isValidBusinessDateAfterDays,
} from '../../../shared/utils/ticket-date-rules.util';

@Component({
  selector: 'app-ticket-tob',
  templateUrl: './ticket-tob.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgClass,
    ReactiveFormsModule,
    MatDatepickerModule,
    MatIconModule,
    MatProgressSpinnerModule,
    SharedModule,
  ],
})
export class TicketTOBComponent implements OnInit {
  readonly TRANSFER_REQUEST = ServiceSelectMode.Transfer;

  readonly CHANGE_REQUEST = 'change';

  private fb = inject(UntypedFormBuilder);

  private cdr = inject(ChangeDetectorRef);

  ServiceSelectMode = ServiceSelectMode;

  validateTransferDate = createBusinessDateValidator(0);

  public tobTxGroup: UntypedFormGroup = this.fb.group({
    requestType: this.fb.control(this.CHANGE_REQUEST, Validators.required),
    reporter: this.fb.control('', Validators.required),
    preferredContactMethod: this.fb.control('', Validators.required),
    countryCode: this.fb.control(''),
    locationOfService: this.fb.control(''),
    service: this.fb.control(''),
    subject: this.fb.control(''),
    description: this.fb.control(''),
    newAccountName: this.fb.control('', Validators.required),
    newFEIN: this.fb.control('', Validators.required),

    newAddress: this.fb.group({
      street1: ['', Validators.required],
      street2: [''],
      city: ['', Validators.required],
      state: ['', Validators.required],
      zip: ['', Validators.required],
    }),

    desiredDate: this.fb.control(null, [
      Validators.required,
      this.validateTransferDate,
    ]),
    additionalComment: this.fb.control(''),
    services: this.fb.array([
      this.fb.group({
        identifyIssueBy: this.fb.control('location'),
        serviceId: this.fb.control(null, Validators.required),
        locationId: this.fb.control(null, Validators.required),
        locationAddress: this.fb.control(null),
        servicesByLocation: this.fb.array([
          this.fb.group({
            id: this.fb.control(''),
            disabled: this.fb.control(''),
            nit: this.fb.control(''),
            product: this.fb.control(''),
            locationId: this.fb.control(''),
            locationName: this.fb.control(''),
          }),
        ]),
      }),
    ]),
  });

  @Input() services: Service[];

  @Input() selectedServiceId: string;

  @Input() submissionError: boolean;

  @Input() errorMessage: string;

  @Input() isSubmitting: boolean;

  @Input() primaryReporters: Contact[];

  @Output() submitForm = new EventEmitter();

  @Output() submitCareForm = new EventEmitter();

  formSubmitAttempted = signal(false);

  inlineErrors: InlineErrors = {};

  areServicesLoading = signal(true);

  areUsersLoading = signal(true);

  readonly customCalHeader = CalendarHeaderComponent;

  startCalendarAt = signal(new Date());

  private readonly fieldErrors: Record<string, string> = {
    reporter: 'Please select a reporter',
    newAccountName: 'Please enter an account name',
    newFEIN: 'Please enter a FEIN',
    'newAddress.street1': 'Please enter a street address',
    'newAddress.city': 'Please enter a city',
    'newAddress.state': 'Please enter a state',
    'newAddress.zip': 'Please enter a zip code',
    desiredDate: 'Please select a transfer date',
    subject: 'Please enter a subject',
    description: 'Please describe your issue',
  };

  ngOnInit(): void {
    this.applyRequestTypeValidators();

    if (this.services) {
      this.areServicesLoading.set(false);
    }

    this.startCalendarAt.set(this.getNextValidDate());
  }

  get isTransferRequest(): boolean {
    return this.tobTxGroup.value.requestType === this.TRANSFER_REQUEST;
  }

  get isChangeRequest(): boolean {
    return this.tobTxGroup.value.requestType === this.CHANGE_REQUEST;
  }

  onRequestTypeChange(): void {
    this.formSubmitAttempted.set(false);
    this.inlineErrors = {};
    this.cdr.markForCheck();
    this.applyRequestTypeValidators();
  }

  private setTransferValidators(enabled: boolean): void {
    const transferControls = [
      'newAccountName',
      'newFEIN',
      'newAddress.street1',
      'newAddress.city',
      'newAddress.state',
      'newAddress.zip',
    ];

    transferControls.forEach((path) => {
      const control = this.tobTxGroup.get(path);
      if (enabled) {
        control?.setValidators(Validators.required);
      } else {
        control?.clearValidators();
      }
      control?.updateValueAndValidity({ emitEvent: false });
    });

    const desiredDate = this.tobTxGroup.get('desiredDate');
    if (enabled) {
      desiredDate?.setValidators([
        Validators.required,
        this.validateTransferDate,
      ]);
    } else {
      desiredDate?.clearValidators();
    }
    desiredDate?.updateValueAndValidity({ emitEvent: false });

    const services = this.tobTxGroup.get('services') as any;
    services?.controls?.forEach((serviceGroup) => {
      serviceGroup
        .get('serviceId')
        ?.setValidators(enabled ? Validators.required : null);
      serviceGroup
        .get('locationId')
        ?.setValidators(enabled ? Validators.required : null);
      serviceGroup
        .get('serviceId')
        ?.updateValueAndValidity({ emitEvent: false });
      serviceGroup
        .get('locationId')
        ?.updateValueAndValidity({ emitEvent: false });
    });
    services?.updateValueAndValidity({ emitEvent: false });
  }

  private setChangeValidators(enabled: boolean): void {
    ['subject', 'description'].forEach((path) => {
      const control = this.tobTxGroup.get(path);
      if (enabled) {
        control?.setValidators(Validators.required);
      } else {
        control?.clearValidators();
      }
      control?.updateValueAndValidity({ emitEvent: false });
    });
  }

  private applyRequestTypeValidators(): void {
    this.setTransferValidators(this.isTransferRequest);
    this.setChangeValidators(this.isChangeRequest);
    this.tobTxGroup.updateValueAndValidity({ emitEvent: false });
  }

  // initialize the calendar to start at the first valid date
  getNextValidDate(): Date {
    return getNextValidBusinessDate(0);
  }

  filterDates = (d: Date): boolean => isValidBusinessDateAfterDays(d, 0);

  isHoliday(calendarDate: Date): boolean {
    return isObservedHoliday(calendarDate);
  }

  canSubmit(): boolean {
    this.applyRequestTypeValidators();
    const defaultMessage = 'Please complete this field.';

    const validateControl = (control: AbstractControl, path: string): void => {
      control.updateValueAndValidity();

      if (!control.valid) {
        this.inlineErrors[path] = this.fieldErrors[path] ?? defaultMessage;
      } else {
        delete this.inlineErrors[path];
      }

      if (control.errors?.pattern) {
        this.inlineErrors[path] = 'pattern';
      } else if (control.errors && path === 'desiredDate') {
        this.inlineErrors[path] = control.errors.required
          ? this.fieldErrors.desiredDate
          : 'Please select a valid transfer date';
      }
    };

    Object.entries(this.tobTxGroup.controls).forEach(([prop, control]) => {
      if (control instanceof UntypedFormGroup) {
        Object.entries(control.controls).forEach(([childProp, childControl]) =>
          validateControl(childControl, `${prop}.${childProp}`),
        );
      } else if (prop === 'services') {
        validateControl(control, prop);
      } else {
        validateControl(control, prop);
      }
    });

    return this.tobTxGroup.valid;
  }

  shouldShowError(path: string): boolean {
    return shouldShowInlineError(
      this.tobTxGroup,
      this.inlineErrors,
      path,
      this.formSubmitAttempted(),
    );
  }

  submitTicket(): void {
    this.formSubmitAttempted.set(true);

    if (this.canSubmit()) {
      if (this.isChangeRequest) {
        const request = {
          Business_Purpose__c: BusinessPurpose.CareOther,
          contactId: this.tobTxGroup.value.reporter,
          portalcustomcontact__c: null,
          description: this.tobTxGroup.value.description,
          service__c: this.tobTxGroup.value.service,
          preferred_contact_method__c:
            this.tobTxGroup.value.preferredContactMethod,
          subject: this.tobTxGroup.value.subject,
        };
        this.submitCareForm.emit(request);
        return;
      }

      const request = {
        order_type: 'Transfer_Of_Billing_Order',
        contactId: this.tobTxGroup.value.reporter,
        preferredContactMethod: this.tobTxGroup.value.preferredContactMethod, // eslint-disable-line

        newAccountName: this.tobTxGroup.value.newAccountName,
        newFEIN: this.tobTxGroup.value.newFEIN,
        newAddress: {
          street1: this.tobTxGroup.get('newAddress.street1').value,
          street2: this.tobTxGroup.get('newAddress.street2').value,
          city: this.tobTxGroup.get('newAddress.city').value,
          state: this.tobTxGroup.get('newAddress.state').value,
          zipCode: this.tobTxGroup.get('newAddress.zip').value,
        },

        desiredDate: this.tobTxGroup.value.desiredDate,
        additionalComment: this.tobTxGroup.value.additionalComment,
        lineItems: this.tobTxGroup.value.services,
      };
      this.submitForm.emit(request);
    }
  }

  usersLoaded(): void {
    this.areUsersLoading.set(false);
  }

  servicesLoaded(): void {
    this.areServicesLoading.set(false);
  }
}
